/**
 * Date utilities with proper undefined/null handling
 * Prevents Sentry serialization errors from undefined dates
 */

/**
 * Safely create a Date object, filtering out undefined values
 */
export function safeDate(value: string | number | Date | undefined | null): Date | null {
  if (value === undefined || value === null) {
    return null;
  }

  try {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Safely convert date to ISO string, handling undefined values
 */
export function safeISOString(value: string | number | Date | undefined | null): string | null {
  const date = safeDate(value);
  return date ? date.toISOString() : null;
}

/**
 * Sanitize object properties that might contain undefined dates
 * Prevents Sentry from attempting to serialize undefined date values
 */
export function sanitizeDateProperties<T extends Record<string, any>>(obj: T): T {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const sanitized = { ...obj };

  // Common date property names that might be undefined
  const dateProperties = [
    'createdAt',
    'updatedAt',
    'scheduledTime',
    'publishedAt',
    'date',
    'timestamp',
    'lastModified',
    'expiresAt',
  ];

  for (const prop of dateProperties) {
    if (prop in sanitized && sanitized[prop] === undefined) {
      (sanitized as Record<string, unknown>)[prop] = null; // Convert undefined to null for safer serialization
    }
  }

  return sanitized;
}

/**
 * Deep sanitize object for undefined date values
 */
export function deepSanitizeDates(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (obj instanceof Date) {
    return isNaN(obj.getTime()) ? null : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepSanitizeDates(item));
  }

  if (typeof obj === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip undefined values entirely to prevent serialization issues
      if (value !== undefined) {
        sanitized[key] = deepSanitizeDates(value);
      }
    }
    return sanitized;
  }

  return obj;
}
